/**
 * The refs three hooks share, named once.
 *
 * `useMatte`, `useMockupBake` and `useLayerAssets` are one coupled knot in AUD-2's split table,
 * and this is the knot: nine refs whose handshakes (ED-5 dirty epochs, ED-6 layer-upload window,
 * F1 restore-epoch consume-once, A3 imperative bake) are the subject of most of the file's
 * postmortem comments. Passing them explicitly is what makes those handshakes reachable from a
 * test instead of only from a mounted 4,449-line component.
 *
 * Every field below is the SAME ref object the component used to declare inline — this is a
 * rename of the owner, not a change of identity or of initial value. Each doc comment travelled
 * here from `MaskEditor.tsx` with the ref it explains; AUD-2 says outright that those comments
 * "are the only reason the file is currently correct."
 *
 * Deliberately NOT here: `pristineRgbaRef`, `scratchRgbaRef`, `scratchMatteRef`, `markerRef`,
 * `originalBytesRef`. Those belong to exactly one hook each (`useCaseImageSource`, `useMatte`) and
 * moving them into a shared bag would widen the surface this split is trying to narrow.
 */
import { useRef, type RefObject } from "react";

export interface EditorRefs {
  /** Offscreen canvas holding the decoded case image (source of pixels). */
  sourceRef: RefObject<HTMLCanvasElement | null>;
  /** Raw AI-matte alpha (255 = kept object/content, 0 = removed) captured on the last removal.
   *  Used by the opt-in "AI edge" refinement to PROTECT content (camera/edges the matte keeps)
   *  from being keyed away by the chroma print mask. Null until a removal has run. */
  matteAlphaRef: RefObject<Uint8ClampedArray | Uint8Array | null>;
  /** Signature (`choke|feather|strength`) of the calibration sliders LAST baked into the source
   *  canvas by applyMatteToSource — lets the live mockup effect re-key from pristine ONLY when a
   *  matte-side slider actually moved (vs a bg/blend/layer tweak), so it doesn't re-run the
   *  erode/feather morphology on every debounced compose. */
  appliedMatteParamsRef: RefObject<string>;
  /** Always holds the CURRENT matte-param signature (choke|feather|strength). The stable
   *  imperative handle (bakeNow) has `[]` deps so it can't read the live slider state directly;
   *  it compares this against `appliedMatteParamsRef` to detect a Save landed mid-debounce. */
  matteParamsSigRef: RefObject<string>;
  /** A3 bake dirtiness: true once a bake-relevant appearance change happens without a following
   *  upload-bake. Set by the appearance-deps effect + any live (uploadBake=false) re-compose;
   *  cleared only when an upload-bake successfully emits `onBaked`. Read via the imperative
   *  `bakeRef.isDirty()` so Save can force a bake before it submits. */
  bakeDirtyRef: RefObject<boolean>;
  /** ED-5 — monotonic counter bumped by `markBakeDirty` on every bake-relevant change. A compose
   *  captures it at START and only clears `bakeDirtyRef` on emit when it is UNCHANGED, so a bake
   *  that finishes after a later edit can't mark that edit as already-uploaded. */
  bakeDirtyEpochRef: RefObject<number>;
  /** ED-5 — tail of the serialized compose chain (see `previewMockup`). */
  composeChainRef: RefObject<Promise<void>>;
  /** ED-6 — the in-flight hosting upload of a CUSTOM layer image (texture / clear-case bg), or
   *  null. A bake that lands inside this window persists `layerRefs` with an UNRESOLVED url, and
   *  because the cloud persist is a full overwrite that ERASES a previously-saved texture url —
   *  with the retry landing after the version locks. `bakeNow` awaits this first, and `isDirty`
   *  reports dirty while it is open so Save always routes through `bakeNow`. */
  layerUploadRef: RefObject<Promise<void> | null>;
  /** F1 restore-vs-dirty handshake: the reopen restore bumps `restoreEpochRef` right before it sets
   *  the saved calibration/AI-edge state. The appearance-deps dirty effect (which fires on the NEXT
   *  render, once those states commit) skips marking dirty exactly once per epoch it hasn't consumed
   *  yet, then records it in `consumedRestoreEpochRef`. So a plain reopen stays bake-clean (no phantom
   *  re-upload of identical assets) while EVERY later real change — epoch already consumed — dirties
   *  as before. Only the restore-driven state transition is suppressed. */
  restoreEpochRef: RefObject<number>;
}

/**
 * Create the nine refs. Called ONCE per mounted editor.
 *
 * Identity is stable across re-renders (that is what `useRef` is for, and every handshake above
 * depends on it), and two calls produce DISJOINT refs — there is no module-level state here, which
 * is the MN-5-shaped bug class this boundary is meant to make impossible to reintroduce.
 */
export function useEditorRefs(): EditorRefs {
  const sourceRef = useRef<HTMLCanvasElement | null>(null);
  const matteAlphaRef = useRef<Uint8ClampedArray | Uint8Array | null>(null);
  const appliedMatteParamsRef = useRef<string>("");
  const matteParamsSigRef = useRef<string>("");
  const bakeDirtyRef = useRef(false);
  const bakeDirtyEpochRef = useRef(0);
  const composeChainRef = useRef<Promise<void>>(Promise.resolve());
  const layerUploadRef = useRef<Promise<void> | null>(null);
  const restoreEpochRef = useRef(0);
  // The bag itself is memoised on a ref too, so `refs` is one stable object across renders and can
  // safely sit in a hook dependency list (J-13 leans on that).
  const bag = useRef<EditorRefs>({
    sourceRef,
    matteAlphaRef,
    appliedMatteParamsRef,
    matteParamsSigRef,
    bakeDirtyRef,
    bakeDirtyEpochRef,
    composeChainRef,
    layerUploadRef,
    restoreEpochRef,
  });
  return bag.current;
}
